/**
 * AdAccountsPage — Subscriber-facing one-click "Connect" dashboard for ad
 * platforms (Meta, Google Ads, LinkedIn Ads, TikTok Ads).
 *
 * What the subscriber sees:
 *   - One card per provider
 *   - "Connect with Google / Meta / LinkedIn / TikTok" branded button
 *   - Once connected: account name, connection date, [Disconnect] [Reconnect]
 *   - If platform admin hasn't registered Konnect with the provider yet:
 *     a gentle "Coming soon — pending platform setup" notice
 *
 * The subscriber NEVER types a key or secret — clicking "Connect" sends them
 * to the provider's official login screen with Konnect's Client ID baked in,
 * and the redirect back saves their personal tokens for THEIR tenant only.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ExternalLink, Unplug, Plug, CheckCircle2, Clock, AlertCircle, Sparkles, ShieldCheck, Activity } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const PROVIDER_STYLE = {
  google_ads: {
    label: 'Connect with Google',
    bg: 'from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800',
    badge: 'bg-yellow-100 text-yellow-800',
    description: 'Pull campaign spend, impressions and clicks from your Google Ads MCC accounts.',
    icon: '🟦',
  },
  meta: {
    label: 'Connect with Meta',
    bg: 'from-[#1877F2] to-[#0866FF] hover:opacity-90',
    badge: 'bg-blue-100 text-blue-800',
    description: 'Manage Facebook Pages, Instagram Business profiles and Ads Manager campaigns.',
    icon: '🟦',
  },
  linkedin_ads: {
    label: 'Connect with LinkedIn',
    bg: 'from-[#0A66C2] to-[#004182] hover:opacity-90',
    badge: 'bg-sky-100 text-sky-800',
    description: 'Read sponsored content performance and Company Page insights.',
    icon: '🟦',
  },
  tiktok_ads: {
    label: 'Connect with TikTok',
    bg: 'from-black to-zinc-900 hover:opacity-90',
    badge: 'bg-zinc-100 text-zinc-900',
    description: 'Connect your TikTok for Business account and track ad performance.',
    icon: '🎵',
  },
};

export default function AdAccountsPage() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/oauth/connections`);
      setConnections(data.connections || []);
    } catch (err) {
      toast.error('Failed to load ad accounts');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // OAuth redirect handler — show a success/error toast and clean the URL
  useEffect(() => {
    const params = new URLSearchParams(search);
    const connected = params.get('connected');
    const errCode = params.get('error');
    const provider = params.get('provider');
    const detail = params.get('detail');
    if (connected) {
      toast.success(`${connected.replace('_', ' ')} connected successfully`);
      navigate('/settings/ad-accounts', { replace: true });
      load();
    } else if (errCode) {
      const friendly = {
        access_denied: `You declined the connection request${provider ? ' on ' + provider : ''}.`,
        oauth_error: `${provider || 'Provider'} returned an error.`,
        invalid_scope: `${provider || 'Provider'} rejected the requested scopes. A Super Admin can adjust them in OAuth Apps settings.`,
        invalid_state: 'Your connection session expired — please click Connect again.',
        missing_params: 'Provider redirect was incomplete — please retry.',
        token_exchange_failed: `Token exchange failed${provider ? ' with ' + provider : ''}.`,
        no_access_token: `${provider || 'Provider'} did not return an access token.`,
      }[errCode] || `OAuth error: ${errCode}`;
      toast.error(friendly, { description: detail || undefined, duration: 9000 });
      navigate('/settings/ad-accounts', { replace: true });
    }
  }, [search, navigate]);

  const handleConnect = async (provider) => {
    setConnecting(provider);
    try {
      const { data } = await axios.get(`${API}/api/oauth/${provider}/authorize`);
      if (data.authorize_url) {
        window.location.href = data.authorize_url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to start OAuth flow';
      toast.error(detail);
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider) => {
    if (!window.confirm('Disconnect this ad account? Konnect will stop syncing data, but your saved token stays on file for a one-click reconnect.')) return;
    try {
      await axios.delete(`${API}/api/oauth/${provider}`);
      toast.success('Disconnected');
      load();
    } catch (err) {
      toast.error('Disconnect failed');
    }
  };

  const handleReconnect = async (provider) => {
    try {
      const { data } = await axios.post(`${API}/api/oauth/${provider}/reconnect`);
      if (data.reconnected || data.already_active) {
        toast.success('Reconnected');
        load();
      }
    } catch (err) {
      // Saved token expired → kick off fresh OAuth flow
      handleConnect(provider);
    }
  };

  const [testing, setTesting] = useState(null);
  const handleTest = async (provider) => {
    setTesting(provider);
    try {
      const { data } = await axios.post(`${API}/api/oauth/${provider}/test`);
      if (data.ok) {
        toast.success(data.message + (data.account_name ? ` · ${data.account_name}` : ''));
      } else {
        toast.error(data.message);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed — try reconnecting');
    } finally {
      setTesting(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto page-shell" data-testid="ad-accounts-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[11px] text-gray-400 uppercase tracking-wider mb-1">
          <Plug className="h-3 w-3" /> Settings · Ad Accounts
        </div>
        <h1 className="text-2xl font-heading font-bold text-gray-900">Connect your ad accounts<PageInfoTooltip pageKey="ad-accounts" /></h1>
        <p className="text-sm text-gray-600 mt-1 max-w-2xl leading-relaxed">
          One click and Konnect starts pulling your campaigns, spend and performance into a single dashboard.
          You stay in full control — disconnect anytime.
        </p>
      </div>

      <Card className="bg-emerald-50 border-emerald-200 mb-6">
        <CardContent className="p-4 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-emerald-900 leading-relaxed">
            <strong className="block mb-0.5">You never share passwords</strong>
            Clicking &ldquo;Connect&rdquo; sends you to the provider&apos;s official login page. After you approve, only an
            access token comes back to Konnect — encrypted, scoped to your account, revocable anytime.
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {connections.map(c => {
            const style = PROVIDER_STYLE[c.provider] || PROVIDER_STYLE.meta;
            const isConnecting = connecting === c.provider;
            return (
              <Card key={c.provider} className="bg-white border-gray-200 hover:shadow-md transition-shadow" data-testid={`ad-card-${c.provider}`}>
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-heading font-bold text-gray-900">{c.name}</h3>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{style.description}</p>
                    </div>
                    {c.connected ? (
                      <Badge className="bg-emerald-100 text-emerald-800 text-[10px]" data-testid={`ad-status-${c.provider}`}>
                        <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Connected
                      </Badge>
                    ) : !c.platform_ready ? (
                      <Badge className="bg-gray-100 text-gray-600 text-[10px]" data-testid={`ad-status-${c.provider}`}>
                        <Clock className="h-2.5 w-2.5 mr-0.5" /> Coming soon
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-700 text-[10px]" data-testid={`ad-status-${c.provider}`}>
                        Not connected
                      </Badge>
                    )}
                  </div>

                  {c.connected && (
                    <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Account</span>
                        <span className="font-semibold text-gray-900 truncate ml-3">{c.account_name || c.account_id || '—'}</span>
                      </div>
                      {c.connected_at && (
                        <div className="flex items-center justify-between text-[10px] text-gray-400">
                          <span>Connected</span>
                          <span>{new Date(c.connected_at).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {!c.platform_ready ? (
                    <div className="text-[11px] text-gray-500 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-1.5">
                      <AlertCircle className="h-3 w-3 text-amber-500 flex-shrink-0 mt-0.5" />
                      Konnect&apos;s platform admin is finalising the {c.name} setup. We&apos;ll email you the moment it&apos;s live.
                    </div>
                  ) : c.connected ? (
                    <div className="space-y-2">
                      {c.last_test_at && (
                        <div className={`text-[10px] flex items-center gap-1 ${c.last_test_ok === false ? 'text-red-600' : c.last_test_ok ? 'text-emerald-700' : 'text-gray-400'}`}>
                          {c.last_test_ok ? <CheckCircle2 className="h-2.5 w-2.5" /> : <AlertCircle className="h-2.5 w-2.5" />}
                          Last health check: {new Date(c.last_test_at).toLocaleString()}
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          variant="outline" size="sm"
                          onClick={() => handleTest(c.provider)}
                          disabled={testing === c.provider}
                          className="text-xs gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
                          data-testid={`test-${c.provider}`}
                        >
                          {testing === c.provider ? <Loader2 className="h-3 w-3 animate-spin" /> : <Activity className="h-3 w-3" />}
                          Test connection
                        </Button>
                        <Button
                          variant="outline" size="sm"
                          onClick={() => handleDisconnect(c.provider)}
                          className="text-xs gap-1.5 text-red-600 hover:bg-red-50 border-red-200"
                          data-testid={`disconnect-${c.provider}`}
                        >
                          <Unplug className="h-3 w-3" />
                          Disconnect
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleConnect(c.provider)}
                          className="text-xs gap-1.5 text-gray-500"
                          data-testid={`reauth-${c.provider}`}
                        >
                          <ExternalLink className="h-3 w-3" />
                          Reauthorize
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      onClick={() => handleConnect(c.provider)}
                      disabled={isConnecting}
                      className={`w-full text-white text-xs gap-2 bg-gradient-to-r ${style.bg}`}
                      data-testid={`connect-${c.provider}`}
                    >
                      {isConnecting ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="h-3.5 w-3.5" />
                      )}
                      {style.label}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="mt-8 text-[11px] text-gray-400 text-center">
        Powered by OAuth 2.0 · Tokens encrypted at rest · Revoke anytime
      </div>
    </div>
  );
}
